import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IoArrowBack, IoBook, IoHeart, IoEye, IoTime, IoPlay, IoPerson } from 'react-icons/io5';
import { useBooks } from '../context/BooksContext';
import { useLibrary } from '../context/LibraryContext';
import { useUser } from '../context/UserContext';
import StarRating from '../components/StarRating';
import SEO from '../components/SEO';
import { estimateBookReadingTime, formatReadingTime } from '../utils/readingTime';
import BookSummary from '../components/BookSummary';
import TipAuthor from '../components/TipAuthor';
import AdBanner from '../components/AdBanner';
import BoostBook from '../components/BoostBook';
import NotFound from './NotFound';
import './BookDetail.css';

export default function BookDetail() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { getBookById, incrementViews } = useBooks();
  const { isInLibrary, addToLibrary, getProgress } = useLibrary();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
    const { user } = useUser();

    const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const book = getBookById(bookId);

  useEffect(() => {
    if (book && user) incrementViews(bookId);
  }, [bookId]);

  if (!book) {
    return <NotFound />;
  }

  const chapters = book.chapters || [];
  const progress = getProgress(bookId);
  const totalTime = estimateBookReadingTime(chapters);
  const genreLabels = { romance: 'Romance', fantasy: 'Fantasy', thriller: 'Thriller', scifi: 'Sci-Fi', drama: 'Drame', horror: 'Horreur', humor: 'Humour', poetry: 'Poésie', adventure: 'Aventure', other: 'Autre' };

  const handleStartReading = () => {
    navigate('/read/' + bookId + (from ? '?from=' + from : ''));
  };

  const handleContinueReading = () => {
    navigate('/read/' + bookId + (from ? '?from=' + from : ''));
  };

  return (
    <div className="bookdetail-page">
      <SEO
  title={book.title}
  description={book.synopsis?.slice(0, 160)}
  image={book.coverImage}
  url={`https://plumly.fr/book/${book.id}`}
  type="book"
/>
      {/* Header avec couverture */}
      <div className="bookdetail-cover-section">
        <div className="bookdetail-cover-bg" style={{
          background: book.coverImage
            ? `url(${book.coverImage}) center/cover no-repeat`
            : book.coverGradient || 'linear-gradient(135deg, #555, #333)'
        }} />
        <div className="bookdetail-cover-overlay" />

        <div className="bookdetail-cover-content">
          <button className="bookdetail-back" onClick={() => navigate(from === 'library' ? '/library' : '/explore')}>
            <IoArrowBack />
          </button>

          <motion.div
            className="bookdetail-cover-img"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: book.coverImage
                ? `url(${book.coverImage}) center/cover no-repeat`
                : book.coverGradient || 'linear-gradient(135deg, #555, #333)'
            }}
          />
        </div>
      </div>

      {/* Infos */}
      <div className="bookdetail-body">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h1 className="bookdetail-title">{book.title}</h1>

          <p className="bookdetail-author" onClick={() => navigate('/author/' + book.authorId)}>
            <IoPerson /> {book.author}
          </p>

          <div className="bookdetail-meta">
            <span className="bookdetail-genre">{genreLabels[book.genre] || book.genre}</span>
            <span><IoHeart /> {(book.likes || 0).toLocaleString('fr-FR')}</span>
            <span><IoEye /> {(book.views || 0).toLocaleString('fr-FR')}</span>
            <span><IoBook /> {chapters.length} chap.</span>
            <span><IoTime /> {formatReadingTime(totalTime)}</span>
          </div>

          <div className="bookdetail-rating">
            <StarRating bookId={bookId} ratings={book.ratings || {}} interactive={false} size="md" />
          </div>
        </motion.div>

        {/* Synopsis */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <h3 className="bookdetail-section-title">Synopsis</h3>
          <p className="bookdetail-synopsis">{book.synopsis || 'Aucun synopsis disponible.'}</p>
        </motion.div>

        {/* Résumé IA */}
        <BookSummary book={book} />

        {/* Note auteur */}
        {book.authorNote && (
          <motion.div className="bookdetail-author-note" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p className="bookdetail-author-note-label">📝 Note de l'auteur</p>
            <p>{book.authorNote}</p>
          </motion.div>
        )}

        {/* Tags */}
        {book.tags && book.tags.length > 0 && (
          <div className="bookdetail-tags">
            {book.tags.map((tag, i) => (
              <span key={i} className="bookdetail-tag">#{tag}</span>
            ))}
          </div>
        )}

{/* Popup connexion obligatoire */}
{showLoginPopup && (
  <div className="login-popup-overlay" onClick={() => setShowLoginPopup(false)}>
    <motion.div
      className="login-popup"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={e => e.stopPropagation()}
    >
      <div className="login-popup-icon">📚</div>
      <h3 className="login-popup-title">Connecte-toi pour continuer</h3>
      <p className="login-popup-text">Crée un compte gratuit pour ajouter des livres à ta bibliothèque et suivre ta progression.</p>
      <button className="login-popup-btn-primary" onClick={() => navigate('/auth')}>
        Se connecter / S'inscrire
      </button>
      <button className="login-popup-btn-secondary" onClick={() => setShowLoginPopup(false)}>
        Pas maintenant
      </button>
    </motion.div>
  </div>
)}

{/* Boutons actions */}
<motion.div className="bookdetail-cta" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
  <button
    className="btn-library"
    onClick={() => {
      if (!user) { setShowLoginPopup(true); return; }
      if (!isInLibrary(bookId)) addToLibrary(book);
    }}
  >
    {user && isInLibrary(bookId) ? '✅ Dans ta bibliothèque' : '📚 Ajouter à ma bibliothèque'}
  </button>
          {progress >= chapters.length - 1 && chapters.length > 0 ? (
            <button className="btn-read-main" onClick={() => navigate('/read/' + bookId + '?ch=0' + (from ? '&from=' + from : ''))}>
              <IoPlay /> Recommencer la lecture
            </button>
          ) : progress > 0 ? (
            <button className="btn-read-main" onClick={handleContinueReading}>
              <IoPlay /> Continuer la lecture — Chapitre {progress + 1}
            </button>
          ) : (
            <button className="btn-read-main" onClick={handleStartReading}>
              <IoPlay /> Commencer la lecture
            </button>
          )}
        </motion.div>

        {/* Soutenir l'auteur + Booster */}
        <div className="bookdetail-actions-row">
<TipAuthor authorName={book.author} authorId={book.authorId?.replace('author_', '') || ''} />
          <BoostBook bookId={bookId} bookTitle={book.title} />
        </div>

        {/* Pub */}
        <AdBanner slot="book-detail" format="horizontal" />

        {/* Liste des chapitres */}
        {chapters.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h3 className="bookdetail-section-title">Chapitres ({chapters.length})</h3>
            <div className="bookdetail-chapters">
              {chapters.map((ch, i) => (
                <button
                  key={ch.id || i}
                  className={'bookdetail-chapter ' + (i <= progress && progress > 0 ? 'bookdetail-chapter-read' : '')}
                  onClick={() => navigate('/read/' + bookId + '?ch=' + i)}
                >
                  <span className="bookdetail-chapter-num">{i + 1}</span>
                  <span className="bookdetail-chapter-title">{ch.title || 'Chapitre ' + (i + 1)}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}